import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../context/LanguageContext';
import { useNotification } from '../../context/NotificationContext';
import { Plus, Edit3, Trash2, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import UniversalList from '../../components/UniversalList';

const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) { localStorage.removeItem('token'); window.location.href = '/login'; throw new Error('Session expired'); }
  return res.json();
};

const StoreAuthorizationList = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const navigate = useNavigate();

  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [storeSearch, setStoreSearch] = useState('');
  const [testingId, setTestingId] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const fetchStores = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      const res = await apiFetch('/api/channel/stores?' + params);
      if (res.status === 'success') {
        setStores(res.data.stores || []);
        setTotal(res.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch stores:', err);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  // Reset page on search
  useEffect(() => { setPage(1); }, [storeSearch]);

  const filteredStores = storeSearch
    ? stores.filter(s => {
        const q = storeSearch.toLowerCase();
        return (s.store_name || '').toLowerCase().includes(q)
          || (s.store_url || '').toLowerCase().includes(q);
      })
    : stores;

  useEffect(() => { fetchStores(); }, [fetchStores]);

  const handleDelete = async (authId) => {
    if (!window.confirm(t('common.confirm_delete'))) return;
    try {
      const res = await apiFetch('/api/channel/stores/delete', { method: 'POST', body: JSON.stringify({ auth_id: authId }) });
      if (res.status === 'success') fetchStores();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleTest = async (authId) => {
    setTestingId(authId);
    setTestResult(null);
    try {
      const res = await apiFetch(`/api/channel/stores/${authId}/test`, { method: 'POST', body: '{}' });
      setTestResult({ id: authId, ...res });
      if (res.success) notify(t('channel.connection_success'), 'success');
      else notify(t('channel.connection_failed'), 'error');
    } catch (err) {
      setTestResult({ id: authId, success: false, message: err.message || 'Connection test failed.' });
      notify(t('channel.connection_failed'), 'error');
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* ── Row 1: Title ── */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">{t('channel.store_authorization')}</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{t('channel.store_authorization_desc')}</p>
      </div>

      {/* ── Row 2: Action buttons ── */}
      <div className="flex items-center gap-2">
        <button onClick={() => navigate('/channel/stores/new')}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors text-sm font-medium">
          <Plus size={18} />{t('common.add')}
        </button>
      </div>

      {/* ── Row 3: Table ── */}
      <UniversalList
        columns={[
          { key: 'store_name', label: t('channel.store_name'), className: 'text-center font-medium' },
          { key: 'channel_name', label: t('channel.channel'), className: 'text-center' },
          { key: 'store_url', label: t('channel.store_url'), className: 'text-center max-w-[200px] truncate' },
          { key: 'store_group_name', label: 'Store Group', className: 'text-center' },
          { key: 'website_name', label: 'Website', className: 'text-center' },
          { key: 'store_code', label: t('channel.store_code'), className: 'text-center' },
          {
            key: 'is_active',
            label: t('channel.sync_enabled'),
            className: 'text-center',
            render: (val) => (
              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                val == 1
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
              }`}>
                {val == 1 ? t('common.yes') : t('common.no')}
              </span>
            ),
          },
          { key: 'last_sync_at', label: t('channel.last_sync'), className: 'text-center', render: (val) => val || '-' },
          {
            key: '_actions',
            label: t('common.actions'),
            headerClassName: 'text-center',
            className: 'text-center',
            render: (_, row) => (
              <div className="flex items-center justify-center gap-1">
                <button onClick={() => handleTest(row.auth_id)} disabled={testingId === row.auth_id}
                  className="p-1.5 rounded-lg text-slate-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors disabled:opacity-50"
                  title={t('channel.test_connection')}>
                  {testingId === row.auth_id
                    ? <RefreshCw size={16} className="animate-spin" />
                    : <RefreshCw size={16} />
                  }
                </button>
                <button onClick={() => navigate(`/channel/stores/edit/${row.auth_id}`)}
                  className="p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  title={t('common.edit')}>
                  <Edit3 size={16} />
                </button>
                <button onClick={() => handleDelete(row.auth_id)}
                  className="p-1.5 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  title={t('common.delete')}>
                  <Trash2 size={16} />
                </button>
              </div>
            ),
          },
        ]}
        data={filteredStores}
        loading={loading}
        emptyMessage={t('channel.no_stores')}
        loadingText={t('common.loading')}
        rowKey="auth_id"
        page={page}
        totalItems={total}
        pageSize={limit}
        setPage={setPage}
        setPageSize={setLimit}
        searchPlaceholder={t('common.search')}
        searchTerm={storeSearch}
        onSearchChange={setStoreSearch}
      />
    </div>
  );
};

export default StoreAuthorizationList;
